/**
 * Cross-tab coordination for notifications.
 *
 * - Primary visible tab owns the SSE stream and toast/sound alerts.
 * - Other tabs receive inbox/count updates over BroadcastChannel.
 * - Per-notification localStorage claims prevent duplicate toasts during ownership races.
 */

import type {
  NotificationCounts,
  NotificationItem,
  NotificationSnapshot,
} from '../../services/api/notifications'

const PRIMARY_TAB_KEY = 'tagp-notification-primary-tab'
const TOAST_CLAIM_PREFIX = 'tagp-notification-toast-claim-'
const TAB_ID_SESSION_KEY = 'tagp-notification-tab-id'
const SYNC_CHANNEL_NAME = 'tagp-notification-sync'
const PRIMARY_TAB_STALE_MS = 5000
const PRIMARY_TAB_HEARTBEAT_MS = 2000
const TOAST_CLAIM_TTL_MS = 3000

interface TabRecord {
  tabId: string
  at: number
}

export type NotificationTabSyncMessage =
  | { type: 'snapshot'; snapshot: NotificationSnapshot }
  | { type: 'notification'; item: NotificationItem }
  | { type: 'counts'; counts: NotificationCounts }
  | { type: 'connected'; connected: boolean }
  | { type: 'mark_read'; id: string; counts: NotificationCounts }
  | { type: 'mark_all_read' }
  | { type: 'mark_seen' }
  | { type: 'dismiss'; id: string }

type OwnershipListener = (isOwner: boolean) => void

function getOrCreateTabId(): string {
  try {
    const existing = sessionStorage.getItem(TAB_ID_SESSION_KEY)
    if (existing) return existing
    const tabId = crypto.randomUUID()
    sessionStorage.setItem(TAB_ID_SESSION_KEY, tabId)
    return tabId
  } catch {
    return 'fallback-tab'
  }
}

const tabId = getOrCreateTabId()
const ownershipListeners = new Set<OwnershipListener>()
let lastKnownOwner = false
let heartbeatTimer: ReturnType<typeof setInterval> | null = null

function readPrimaryRecord(): TabRecord | null {
  try {
    const raw = localStorage.getItem(PRIMARY_TAB_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as TabRecord
    if (!parsed?.tabId || typeof parsed.at !== 'number') return null
    return parsed
  } catch {
    return null
  }
}

function writePrimaryRecord(): void {
  try {
    localStorage.setItem(PRIMARY_TAB_KEY, JSON.stringify({ tabId, at: Date.now() }))
  } catch {
    /* storage unavailable */
  }
}

function clearPrimaryRecordIfOwned(): void {
  const record = readPrimaryRecord()
  if (record?.tabId === tabId) {
    try {
      localStorage.removeItem(PRIMARY_TAB_KEY)
    } catch {
      /* storage unavailable */
    }
  }
}

function isPrimaryVisibleTab(): boolean {
  if (document.visibilityState !== 'visible') return false

  const record = readPrimaryRecord()
  if (!record) return true
  if (record.tabId === tabId) return true
  if (Date.now() - record.at > PRIMARY_TAB_STALE_MS) return true
  return false
}

function notifyOwnershipListeners(): void {
  const owner = isPrimaryVisibleTab()
  if (owner === lastKnownOwner) return
  lastKnownOwner = owner
  for (const listener of ownershipListeners) {
    listener(owner)
  }
}

function maybeClaimOrHeartbeat(): void {
  if (document.visibilityState === 'visible') {
    const record = readPrimaryRecord()
    if (!record || record.tabId === tabId || Date.now() - record.at > PRIMARY_TAB_STALE_MS) {
      writePrimaryRecord()
    }
  } else {
    clearPrimaryRecordIfOwned()
  }
  notifyOwnershipListeners()
}

function claimToastForNotification(notificationKey: string): boolean {
  const storageKey = `${TOAST_CLAIM_PREFIX}${notificationKey}`
  const now = Date.now()

  try {
    const raw = localStorage.getItem(storageKey)
    if (raw) {
      const claim = JSON.parse(raw) as TabRecord
      if (claim.tabId !== tabId && now - claim.at < TOAST_CLAIM_TTL_MS) {
        return false
      }
    }
    localStorage.setItem(storageKey, JSON.stringify({ tabId, at: now }))
    return true
  } catch {
    return document.visibilityState === 'visible'
  }
}

function handleVisibilityChange(): void {
  maybeClaimOrHeartbeat()
}

function handleStorageEvent(event: StorageEvent): void {
  if (event.key !== PRIMARY_TAB_KEY) return
  if (document.visibilityState !== 'visible') {
    notifyOwnershipListeners()
    return
  }
  if (!event.newValue) {
    writePrimaryRecord()
  }
  notifyOwnershipListeners()
}

function createSyncChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  try {
    return new BroadcastChannel(SYNC_CHANNEL_NAME)
  } catch {
    return null
  }
}

/** Register tab visibility / ownership listeners. Returns a cleanup function. */
export function initNotificationToastTabGate(): () => void {
  maybeClaimOrHeartbeat()
  document.addEventListener('visibilitychange', handleVisibilityChange)
  window.addEventListener('storage', handleStorageEvent)
  if (!heartbeatTimer) {
    heartbeatTimer = setInterval(maybeClaimOrHeartbeat, PRIMARY_TAB_HEARTBEAT_MS)
  }
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('storage', handleStorageEvent)
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
    clearPrimaryRecordIfOwned()
    lastKnownOwner = false
  }
}

/** True when this tab currently owns the notification SSE stream. */
export function isNotificationStreamOwner(): boolean {
  return isPrimaryVisibleTab()
}

/**
 * Subscribe to stream-ownership changes for this tab.
 * Invokes immediately with the current ownership state.
 */
export function subscribeNotificationStreamOwnership(listener: OwnershipListener): () => void {
  ownershipListeners.add(listener)
  const owner = isPrimaryVisibleTab()
  lastKnownOwner = owner
  listener(owner)
  return () => {
    ownershipListeners.delete(listener)
  }
}

/** Publish a notification state update to other tabs. */
export function publishNotificationTabSync(message: NotificationTabSyncMessage): void {
  const channel = createSyncChannel()
  if (!channel) return
  try {
    channel.postMessage(message)
  } catch {
    /* channel unavailable */
  } finally {
    try {
      channel.close()
    } catch {
      /* ignore */
    }
  }
}

/** Subscribe to cross-tab notification state updates. */
export function subscribeNotificationTabSync(
  handler: (message: NotificationTabSyncMessage) => void,
): () => void {
  const channel = createSyncChannel()
  if (!channel) return () => undefined

  const onMessage = (event: MessageEvent<NotificationTabSyncMessage>) => {
    if (!event.data || typeof event.data !== 'object' || !('type' in event.data)) return
    handler(event.data)
  }
  channel.addEventListener('message', onMessage)
  return () => {
    channel.removeEventListener('message', onMessage)
    try {
      channel.close()
    } catch {
      /* ignore */
    }
  }
}

/** True when this tab should show toast/sound for the given notification. */
export function shouldShowNotificationToastInThisTab(notificationKey: string): boolean {
  if (!isPrimaryVisibleTab()) return false
  return claimToastForNotification(notificationKey)
}

/** Test helpers */
export const __testing = {
  tabId,
  PRIMARY_TAB_KEY,
  TOAST_CLAIM_PREFIX,
  PRIMARY_TAB_STALE_MS,
  PRIMARY_TAB_HEARTBEAT_MS,
  SYNC_CHANNEL_NAME,
  readPrimaryRecord,
  writePrimaryRecord,
  clearPrimaryRecordIfOwned,
  isPrimaryVisibleTab,
  claimToastForNotification,
  maybeClaimOrHeartbeat,
  resetOwnershipState: () => {
    ownershipListeners.clear()
    lastKnownOwner = false
  },
}
